import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Tooltip,
  Alert,
  useTheme,
  FormControl,
  FormControlLabel,
  Switch,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  SelectChangeEvent,
} from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { tokens } from '../../theme';
import { FilterContext, IFilterState } from '../../contexts/FilterContext';
import { ResponsiveLine } from '@nivo/line';
import { ResponsiveBar } from '@nivo/bar';
import { api } from '../../helpers/apiUtils';
import { determineAggregation, getFormattedTick, AggregationType } from '../../helpers/chartUtils';
import TableWidget, { HeaderMappingItem } from '../../helpers/tableBuilder';
import EtkpDetailModal from './EtkpDetailModal';
import moment from 'moment';
import {
  MAILING_CATEGORIES,
  CATEGORY_LABELS,
  REVENUE_ATTRIBUTED_CATEGORIES,
  NEWSLETTER_ZONES,
  ZONE_LABELS,
  isNewsletterZone,
  type NewsletterZone,
  type ZoneRevenue,
} from '../../helpers/mailingCategories';

interface EtkpDataRow {
  period: string;
  emails_sent: number;
  revenue: number;
  etkp: number;
  newsletter_zones?: ZoneRevenue;
  // Newsletter-only recipient count for the period — drives correct
  // newsletter-scoped ETKP. emails_sent above sums across all selected
  // categories and is wrong as a denominator when the user has more than
  // newsletter selected.
  newsletter_recipients?: number;
}

interface EtkpSummary {
  total_emails_sent: number;
  total_revenue: number;
  overall_etkp: number;
  newsletter_zones?: ZoneRevenue;
  newsletter_recipients?: number;
}

interface CustomerOption {
  id: string;
  name: string;
}

// Data before Aug 2025 is unreliable due to ESP migration gap (MC → Iterable)
const ETKP_MIN_DATE = new Date('2025-08-01');

const fetchEtkpData = async (
  startDate: Date | null,
  endDate: Date | null,
  aggregation: AggregationType,
  selectedCustomers: string[],
  selectedMailingTypes: string[],
  selectedZones: string[],
  normalize = false,
) => {
  if (!startDate || !endDate) return { data: [], summary: null };

  const effectiveStart = startDate < ETKP_MIN_DATE ? ETKP_MIN_DATE : startDate;

  const params = new URLSearchParams({
    start_date: moment(effectiveStart).format('YYYY-MM-DD'),
    end_date: moment(endDate).format('YYYY-MM-DD'),
    aggregation,
  });
  selectedCustomers.forEach((c) => params.append('customer_id', c));
  // Always send when not "all" — including the empty case, so the backend
  // can distinguish "user chose none" (zero rows) from "no filter applied"
  // (all 10 categories).
  if (selectedMailingTypes.length < MAILING_CATEGORIES.length) {
    params.append('mailing_types', selectedMailingTypes.join(','));
  }
  // Same undefined-vs-empty distinction for zones. Only send when newsletter
  // category is in the type filter — otherwise the param is a no-op.
  if (
    selectedMailingTypes.includes('newsletter') &&
    selectedZones.length < NEWSLETTER_ZONES.length
  ) {
    params.append('nl_zones', selectedZones.join(','));
  }
  if (normalize) params.append('normalize', '1');

  const response = await api.get(`/api/etkp-development?${params}`);
  return response.data;
};

const EtkpDevelopment = () => {
  const { filterState } = useContext(FilterContext)!;
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<EtkpDataRow[]>([]);
  const [summary, setSummary] = useState<EtkpSummary | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<string | null>(null);
  const [availableCustomers, setAvailableCustomers] = useState<CustomerOption[]>([]);
  // Hydrate filter state from URL on mount. Missing `mailing_types` →
  // default to the 7 revenue-attributed categories (newsletter / followup /
  // other hidden by default — they inflate sends without adding revenue).
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>(() => {
    const initial = new URLSearchParams(window.location.search);
    return initial.getAll('customer_id');
  });
  const [selectedMailingTypes, setSelectedMailingTypes] = useState<string[]>(() => {
    const raw = new URLSearchParams(window.location.search).get('mailing_types');
    if (raw === null) return [...REVENUE_ATTRIBUTED_CATEGORIES];
    if (raw === '') return [];
    const valid = (MAILING_CATEGORIES as readonly string[]).filter((c) =>
      raw.split(',').includes(c),
    );
    return valid;
  });
  const [normalizeInactive, setNormalizeInactive] = useState<boolean>(
    () => new URLSearchParams(window.location.search).get('normalize') === '1',
  );
  // Newsletter zone filter — defaults to all 6 (= no filter). Only meaningful
  // when 'newsletter' is in selectedMailingTypes; otherwise the backend
  // ignores nl_zones.
  const [selectedZones, setSelectedZones] = useState<string[]>(() => {
    const raw = new URLSearchParams(window.location.search).get('nl_zones');
    if (raw === null) return [...NEWSLETTER_ZONES];
    if (raw === '') return [];
    return (NEWSLETTER_ZONES as readonly string[]).filter((z) => raw.split(',').includes(z));
  });

  // Reflect page-local filters to URL. Merges into the existing params so any
  // other hook (e.g. FilterBar's useFilterUrlSync) that writes dates doesn't
  // wipe our keys — we read `searchParams` so this effect re-runs after
  // external writes.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const isAttributedDefault =
      selectedMailingTypes.length === REVENUE_ATTRIBUTED_CATEGORIES.length &&
      REVENUE_ATTRIBUTED_CATEGORIES.every((c) => selectedMailingTypes.includes(c));
    next.delete('mailing_types');
    if (!isAttributedDefault) {
      next.set('mailing_types', selectedMailingTypes.join(','));
    }
    next.delete('customer_id');
    selectedCustomers.forEach((c) => next.append('customer_id', c));
    next.delete('normalize');
    if (normalizeInactive) next.set('normalize', '1');
    next.delete('nl_zones');
    if (selectedZones.length < NEWSLETTER_ZONES.length) {
      next.set('nl_zones', selectedZones.join(','));
    }
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [
    selectedMailingTypes,
    selectedCustomers,
    selectedZones,
    normalizeInactive,
    searchParams,
    setSearchParams,
  ]);

  const aggregation = useMemo((): AggregationType => {
    if (!filterState.startDate || !filterState.endDate) return 'monthly';
    return determineAggregation(filterState.startDate, filterState.endDate);
  }, [filterState.startDate, filterState.endDate]);

  // Fetch available customers when dates OR mailing-type filter changes —
  // the dropdown should only list customers that actually have data in the
  // active slice, otherwise users can select a dead-end combination.
  useEffect(() => {
    let cancelled = false;
    const loadCustomers = async () => {
      if (!filterState.startDate || !filterState.endDate) return;
      const effectiveStart =
        filterState.startDate < ETKP_MIN_DATE ? ETKP_MIN_DATE : filterState.startDate;
      try {
        const params = new URLSearchParams({
          start_date: moment(effectiveStart).format('YYYY-MM-DD'),
          end_date: moment(filterState.endDate).format('YYYY-MM-DD'),
        });
        if (selectedMailingTypes.length < MAILING_CATEGORIES.length) {
          params.append('mailing_types', selectedMailingTypes.join(','));
        }
        const response = await api.get(`/api/etkp-development/available-customers?${params}`);
        if (!cancelled) {
          const customers: CustomerOption[] = response.data.data || [];
          setAvailableCustomers(customers);
          // Remove any selected customers that are no longer available
          setSelectedCustomers((prev) => prev.filter((id) => customers.some((c) => c.id === id)));
        }
      } catch (error) {
        console.error('Error fetching available customers:', error);
      }
    };
    loadCustomers();
    return () => {
      cancelled = true;
    };
  }, [filterState.startDate, filterState.endDate, selectedMailingTypes]);

  // Fetch data for KPI cards and chart
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!filterState.startDate || !filterState.endDate) return;
      setLoading(true);
      try {
        const result = await fetchEtkpData(
          filterState.startDate,
          filterState.endDate,
          aggregation,
          selectedCustomers,
          selectedMailingTypes,
          selectedZones,
          normalizeInactive,
        );
        if (!cancelled) {
          setData(result.data || []);
          setSummary(result.summary || null);
        }
      } catch (error) {
        console.error('Error fetching ETKP data:', error);
        if (!cancelled) {
          setData([]);
          setSummary(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [
    filterState.startDate,
    filterState.endDate,
    selectedCustomers,
    selectedMailingTypes,
    selectedZones,
    aggregation,
    normalizeInactive,
  ]);

  // Chart data with dual-axis normalization
  const chartData = useMemo(() => {
    if (data.length === 0) return [];

    const maxEtkp = Math.max(...data.map((d) => Number(d.etkp) || 0));
    const maxSends = Math.max(...data.map((d) => Number(d.emails_sent) || 0));

    if (maxEtkp === 0) return [];

    return [
      {
        id: 'ETKP (€)',
        data: data.map((d) => ({
          x: d.period,
          y: Number(d.etkp) || 0,
          originalValue: Number(d.etkp) || 0,
        })),
      },
      {
        id: 'E-Mails Versendet',
        data: data.map((d) => {
          const sends = Number(d.emails_sent) || 0;
          return {
            x: d.period,
            y: maxSends > 0 ? (sends / maxSends) * maxEtkp : 0,
            originalValue: sends,
          };
        }),
      },
    ];
  }, [data]);

  // For right axis denormalization
  const maxSends = useMemo(() => {
    if (data.length === 0) return 0;
    return Math.max(...data.map((d) => Number(d.emails_sent) || 0));
  }, [data]);

  const maxEtkp = useMemo(() => {
    if (data.length === 0) return 0;
    return Math.max(...data.map((d) => Number(d.etkp) || 0));
  }, [data]);

  const dateWasClamped = useMemo(() => {
    if (!filterState.startDate) return false;
    return filterState.startDate < ETKP_MIN_DATE;
  }, [filterState.startDate]);

  // getData for TableWidget
  const getTableData = async (fs: IFilterState): Promise<EtkpDataRow[]> => {
    if (!fs.startDate || !fs.endDate) return [];
    const agg = determineAggregation(fs.startDate, fs.endDate);
    const result = await fetchEtkpData(
      fs.startDate,
      fs.endDate,
      agg,
      selectedCustomers,
      selectedMailingTypes,
      selectedZones,
      normalizeInactive,
    );
    return result.data || [];
  };

  const computeSummary = (tableData: EtkpDataRow[]): EtkpDataRow => {
    if (!tableData.length) {
      return { period: 'Insgesamt', emails_sent: 0, revenue: 0, etkp: 0 };
    }
    const totalSent = tableData.reduce((sum, d) => sum + (Number(d.emails_sent) || 0), 0);
    const totalRevenue = tableData.reduce((sum, d) => sum + (Number(d.revenue) || 0), 0);
    return {
      period: 'Insgesamt',
      emails_sent: totalSent,
      revenue: Number(totalRevenue.toFixed(2)),
      etkp: totalSent > 0 ? Number(((totalRevenue / totalSent) * 1000).toFixed(2)) : 0,
    };
  };

  const detailsHeaderMapping: Record<string, HeaderMappingItem> = {
    period: { name: 'Zeitraum', type: 'string' },
    emails_sent: { name: 'E-Mails Versendet', type: 'number', digits: 0 },
    revenue: { name: 'Umsatz (€)', type: 'number', digits: 2 },
    etkp: { name: 'ETKP (€)', type: 'number', digits: 2 },
  };

  return (
    <Box m="20px">
      {/* Explanation Box */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          backgroundColor: colors.blueAccent[800],
          border: `1px solid ${colors.blueAccent[600]}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <InfoOutlined sx={{ color: colors.blueAccent[400], fontSize: 24, mt: 0.5 }} />
          <Box>
            <Typography variant="h6" sx={{ color: colors.blueAccent[400], mb: 1 }}>
              ETKP Entwicklung: Erklärung
            </Typography>
            <Typography variant="body2" sx={{ color: colors.grey[100], lineHeight: 1.6 }}>
              <strong>ETKP = Erlös pro Tausend Kontakte (Revenue per 1.000 Recipients)</strong>
              <br />• <strong>E-Mails Versendet:</strong> Alle Iterable-Blasts — VNR SAMs, Webinar
              Heat-Up/Redlink, PLX Direct Mailings, Non-Opener-Sends, Followups, Newsletter und
              Sonstige
              <br />• <strong>Umsatz:</strong> Live-Attribution pro Kategorie (VNR SAMs →
              DOI-Payout, Webinar → CPA, PLX → Banner-Regex). Newsletter (FT) seit 2026-05-07 mit
              4-Quellen-Live-Attribution: Webinar-Regs × 90d-rolling-CPA + PLX-Bestellungen +
              VNR-Inline (LPAI-Banner) + VNR-Slot-Ads (Checkpoint/Bottom/PS/HTML — via C-ID-Match).
              Followup/Sonstige weiterhin ohne systematische Attribution
              <br />• <strong>ETKP-Formel:</strong> (Gesamtumsatz / Gesamt-E-Mails) × 1.000
              <br />• <strong>Versand-Typ-Filter:</strong> Für einheitliche ETKP-Vergleiche
              Kategorien ohne Revenue-Attribution ausblenden — sonst wird Gesamt-ETKP durch
              Followup/Sonstige verwässert
              <br />• <strong>Daten ab:</strong> 01.08.2025 — Ältere Daten sind durch die
              ESP-Migration (Mailchimp → Iterable) nicht zuverlässig
            </Typography>
          </Box>
        </Box>
      </Paper>

      {dateWasClamped && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Startdatum wurde automatisch auf den 01.08.2025 angepasst. Ältere Daten sind durch die
          ESP-Migration (Mailchimp → Iterable) nicht verfügbar.
        </Alert>
      )}

      {/* Filters: customer + mailing type */}
      <Box mb={3} display="flex" gap={2} flexWrap="wrap">
        {availableCustomers.length > 0 && (
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Kunde</InputLabel>
            <Select
              multiple
              value={selectedCustomers}
              onChange={(e: SelectChangeEvent<string[]>) => {
                const value =
                  typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                if (value.includes('__clear__')) {
                  setSelectedCustomers([]);
                } else {
                  setSelectedCustomers(value);
                }
              }}
              input={<OutlinedInput label="Kunde" />}
              renderValue={(selected) => {
                if (selected.length === 0) return 'Alle';
                return selected
                  .map((id) => availableCustomers.find((c) => c.id === id)?.name || id)
                  .join(', ');
              }}
              MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
            >
              <MenuItem value="__clear__">
                <em>Alle (kein Filter)</em>
              </MenuItem>
              {availableCustomers.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  <Checkbox checked={selectedCustomers.includes(c.id)} />
                  <ListItemText primary={`${c.name} (${c.id})`} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <FormControl sx={{ minWidth: 260 }}>
          <InputLabel>Versand-Typ</InputLabel>
          <Select
            multiple
            value={selectedMailingTypes}
            onChange={(e: SelectChangeEvent<string[]>) => {
              const value =
                typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
              if (value.includes('__all__')) {
                setSelectedMailingTypes([...MAILING_CATEGORIES]);
              } else if (value.includes('__none__')) {
                setSelectedMailingTypes([]);
              } else {
                setSelectedMailingTypes(value);
              }
            }}
            input={<OutlinedInput label="Versand-Typ" />}
            renderValue={(selected) => {
              if (selected.length === 0) return 'Keiner';
              if (selected.length === MAILING_CATEGORIES.length) return 'Alle';
              return selected.map((c) => CATEGORY_LABELS[c] || c).join(', ');
            }}
            MenuProps={{ PaperProps: { style: { maxHeight: 400 } } }}
          >
            <MenuItem value="__all__">
              <em>Alle auswählen</em>
            </MenuItem>
            <MenuItem value="__none__">
              <em>Keiner</em>
            </MenuItem>
            {MAILING_CATEGORIES.map((c) => (
              <MenuItem key={c} value={c}>
                <Checkbox checked={selectedMailingTypes.includes(c)} />
                <ListItemText primary={CATEGORY_LABELS[c]} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Newsletter zone filter — only enabled when newsletter category is
            in selection. Greyed-out otherwise (would be a no-op). */}
        <FormControl sx={{ minWidth: 240 }} disabled={!selectedMailingTypes.includes('newsletter')}>
          <InputLabel>Newsletter-Zonen</InputLabel>
          <Select
            multiple
            value={selectedZones}
            onChange={(e: SelectChangeEvent<string[]>) => {
              const value =
                typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
              if (value.includes('__all__')) {
                setSelectedZones([...NEWSLETTER_ZONES]);
              } else if (value.includes('__none__')) {
                setSelectedZones([]);
              } else {
                setSelectedZones(value.filter(isNewsletterZone));
              }
            }}
            input={<OutlinedInput label="Newsletter-Zonen" />}
            renderValue={(selected) => {
              if (selected.length === 0) return 'Keine';
              if (selected.length === NEWSLETTER_ZONES.length) return 'Alle';
              return selected.map((z) => ZONE_LABELS[z] || z).join(', ');
            }}
            MenuProps={{ PaperProps: { style: { maxHeight: 400 } } }}
          >
            <MenuItem value="__all__">
              <em>Alle auswählen</em>
            </MenuItem>
            <MenuItem value="__none__">
              <em>Keine</em>
            </MenuItem>
            {NEWSLETTER_ZONES.map((z) => (
              <MenuItem key={z} value={z}>
                <Checkbox checked={selectedZones.includes(z)} />
                <ListItemText primary={ZONE_LABELS[z]} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Normalize inactive toggle */}
      <Box mb={3} display="flex" alignItems="center">
        <FormControlLabel
          control={
            <Switch
              checked={normalizeInactive}
              onChange={(e) => setNormalizeInactive(e.target.checked)}
              size="small"
            />
          }
          label="Inaktive normalisieren"
        />
        <Tooltip title="Samstags-Sends mit Inaktiven werden auf den Durchschnitt der letzten 10 normalen Sends normalisiert, um faire ETKP-Vergleiche zu ermöglichen.">
          <InfoOutlined
            sx={{
              fontSize: 16,
              ml: 0.5,
              color: 'text.secondary',
              cursor: 'help',
            }}
          />
        </Tooltip>
      </Box>

      {/* KPI Summary Cards */}
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="200px">
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={3} mb={3}>
            <Grid item xs={12} sm={4}>
              <Card sx={{ backgroundColor: colors.primary[400] }}>
                <CardContent>
                  <Typography
                    variant="h6"
                    color={colors.grey[100]}
                    gutterBottom
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    Gesamt E-Mails Versendet
                    <Tooltip
                      title={
                        <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                          <strong>Summe aller versendeten E-Mails im Zeitraum</strong>
                          <br />
                          Quellen: Automatisierte SAMs (mc_sams_statistics.recipients) +
                          Webinar/PLX-Mailings (iterable_campaign_metrics_cache.total_sends)
                        </div>
                      }
                    >
                      <InfoOutlined
                        sx={{
                          fontSize: 16,
                          ml: 1,
                          color: colors.grey[300],
                          cursor: 'help',
                        }}
                      />
                    </Tooltip>
                  </Typography>
                  <Typography variant="h3" fontWeight="600" color={colors.greenAccent[400]}>
                    {summary?.total_emails_sent?.toLocaleString('de-DE') || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{ backgroundColor: colors.primary[400] }}>
                <CardContent>
                  <Typography
                    variant="h6"
                    color={colors.grey[100]}
                    gutterBottom
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    Gesamtumsatz
                    <Tooltip
                      title={
                        <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                          <strong>Gesamter zugeordneter Umsatz im Zeitraum</strong>
                          <br />
                          SAMs: mc_sams_statistics.revenue
                          <br />
                          Mailings: mailing_timeline_metrics.cpa_revenue (stündlicher Cache)
                        </div>
                      }
                    >
                      <InfoOutlined
                        sx={{
                          fontSize: 16,
                          ml: 1,
                          color: colors.grey[300],
                          cursor: 'help',
                        }}
                      />
                    </Tooltip>
                  </Typography>
                  <Typography variant="h3" fontWeight="600" color={colors.greenAccent[400]}>
                    €
                    {summary?.total_revenue?.toLocaleString('de-DE', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }) || '0,00'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{ backgroundColor: colors.primary[400] }}>
                <CardContent>
                  <Typography
                    variant="h6"
                    color={colors.grey[100]}
                    gutterBottom
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    Gesamt-ETKP
                    <Tooltip
                      title={
                        <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                          <strong>Erlös pro Tausend Kontakte (gesamter Zeitraum)</strong>
                          <br />
                          Formel: (Gesamtumsatz / Gesamt-E-Mails) × 1.000
                          <br />
                          Höherer Wert = mehr Umsatz pro versendetem Kontakt
                        </div>
                      }
                    >
                      <InfoOutlined
                        sx={{
                          fontSize: 16,
                          ml: 1,
                          color: colors.grey[300],
                          cursor: 'help',
                        }}
                      />
                    </Tooltip>
                  </Typography>
                  <Typography variant="h3" fontWeight="600" color={colors.greenAccent[400]}>
                    €
                    {summary?.overall_etkp?.toLocaleString('de-DE', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }) || '0,00'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Nivo Line Chart */}
          <Paper elevation={3} sx={{ p: 2, mb: 3, backgroundColor: colors.primary[400] }}>
            <Typography variant="h5" mb={2} color={colors.grey[100]}>
              ETKP Entwicklung
            </Typography>
            <Box height="400px">
              {chartData.length > 0 ? (
                <ResponsiveLine
                  data={chartData}
                  theme={{
                    axis: {
                      domain: {
                        line: { stroke: colors.grey[100] },
                      },
                      legend: {
                        text: { fill: colors.grey[100] },
                      },
                      ticks: {
                        line: { stroke: colors.grey[100], strokeWidth: 1 },
                        text: { fill: colors.grey[100] },
                      },
                    },
                    legends: {
                      text: { fill: colors.grey[100] },
                    },
                    tooltip: {
                      container: { color: colors.primary[500] },
                    },
                  }}
                  colors={[colors.greenAccent[500], 'rgba(141, 179, 227, 0.35)']}
                  lineWidth={3}
                  enableArea={true}
                  areaOpacity={0.08}
                  defs={[
                    {
                      id: 'areaGradientEtkp',
                      type: 'linearGradient',
                      colors: [
                        {
                          offset: 0,
                          color: colors.greenAccent[500],
                          opacity: 0.2,
                        },
                        {
                          offset: 100,
                          color: colors.greenAccent[500],
                          opacity: 0,
                        },
                      ],
                    },
                    {
                      id: 'areaHidden',
                      type: 'linearGradient',
                      colors: [
                        { offset: 0, color: '#000', opacity: 0 },
                        { offset: 100, color: '#000', opacity: 0 },
                      ],
                    },
                  ]}
                  fill={[
                    { match: { id: 'ETKP (€)' }, id: 'areaGradientEtkp' },
                    { match: { id: 'E-Mails Versendet' }, id: 'areaHidden' },
                  ]}
                  margin={{ top: 50, right: 140, bottom: 50, left: 60 }}
                  xScale={{ type: 'point' }}
                  yScale={{
                    type: 'linear',
                    min: 0,
                    max: 'auto',
                    stacked: false,
                    reverse: false,
                  }}
                  tooltip={({ point }) => {
                    const originalValue = (point.data as any).originalValue;
                    const isCurrency = point.serieId === 'ETKP (€)';
                    const formattedValue = isCurrency
                      ? `€${originalValue?.toLocaleString('de-DE', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}`
                      : originalValue?.toLocaleString('de-DE');

                    return (
                      <div
                        style={{
                          background: colors.primary[400],
                          padding: '9px 12px',
                          border: `1px solid ${colors.grey[100]}`,
                          borderRadius: '4px',
                          color: colors.grey[100],
                        }}
                      >
                        <strong>{point.serieId}</strong>
                        <br />
                        Zeitraum: {getFormattedTick(aggregation, String(point.data.x))}
                        <br />
                        {isCurrency ? 'Betrag' : 'Anzahl'}: {formattedValue}
                      </div>
                    );
                  }}
                  axisTop={null}
                  axisRight={
                    maxSends > 0
                      ? {
                          tickSize: 5,
                          tickPadding: 5,
                          tickRotation: 0,
                          legend: 'E-Mails Versendet',
                          legendOffset: 55,
                          legendPosition: 'middle',
                          format: (value: number) => {
                            if (maxEtkp === 0) return '0';
                            const denormalized = Math.round((value / maxEtkp) * maxSends);
                            return denormalized.toLocaleString('de-DE');
                          },
                        }
                      : null
                  }
                  axisBottom={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: -45,
                    legend: 'Zeitraum',
                    legendOffset: 45,
                    legendPosition: 'middle',
                    format: (value) => getFormattedTick(aggregation, String(value)),
                  }}
                  axisLeft={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                    legend: 'ETKP (€)',
                    legendOffset: -50,
                    legendPosition: 'middle',
                    format: (value: number) => `€${value}`,
                  }}
                  pointSize={10}
                  pointColor={{ theme: 'background' }}
                  pointBorderWidth={2}
                  pointBorderColor={{ from: 'serieColor' }}
                  pointLabelYOffset={-12}
                  useMesh={true}
                  legends={[
                    {
                      anchor: 'bottom-right',
                      direction: 'column',
                      justify: false,
                      translateX: 130,
                      translateY: 0,
                      itemsSpacing: 0,
                      itemDirection: 'left-to-right',
                      itemWidth: 150,
                      itemHeight: 20,
                      itemOpacity: 0.75,
                      symbolSize: 12,
                      symbolShape: 'circle',
                      symbolBorderColor: 'rgba(0, 0, 0, .5)',
                      effects: [
                        {
                          on: 'hover',
                          style: {
                            itemBackground: 'rgba(0, 0, 0, .03)',
                            itemOpacity: 1,
                          },
                        },
                      ],
                    },
                  ]}
                />
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <Typography color={colors.grey[300]}>Keine Daten verfügbar</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </>
      )}

      {/* Table */}
      <TableWidget<EtkpDataRow>
        key={`${selectedCustomers.join(',')}-${selectedMailingTypes.join(',')}-${selectedZones.join(',')}-${normalizeInactive}`}
        getData={getTableData}
        computeSummary={computeSummary}
        detailsHeaderMapping={detailsHeaderMapping}
        initialSortColumn="period"
        sortOrder="desc"
        excelDownloadName="ETKP-Entwicklung"
        onRowClick={(row) => setSelectedPeriod(row.period)}
      />

      {/* Newsletter zone breakdown — only renders when newsletter is in the
          type filter AND we have non-zero zone data. Each card shows the
          slot's revenue contribution AND its ETKP share (= zone_revenue /
          total_emails_sent × 1000 — sum of all zones equals overall NL ETKP). */}
      {summary?.newsletter_zones && selectedMailingTypes.includes('newsletter') ? (
        <Paper elevation={3} sx={{ p: 2, mt: 3, mb: 3, backgroundColor: colors.primary[400] }}>
          <Box display="flex" alignItems="center" mb={1.5}>
            <Typography variant="h5" color={colors.grey[100]}>
              Newsletter — Revenue & ETKP nach Zone
            </Typography>
            <Tooltip title="FT-Newsletter-Performance je Slot-Position. KI Ad = AI-injizierte Inline-Werbung. Checkpoint / Native / PS / HTML unten = FTWP-injizierte Slot-Zonen. ETKP je Zone = (Revenue der Zone / Gesamtempfänger) × 1000 — die Summe aller Zone-ETKPs ergibt die Gesamt-Newsletter-ETKP. Zone-Filter (oben) schränkt Σ-Revenue ein, nicht die Aufschlüsselung.">
              <InfoOutlined
                sx={{
                  fontSize: 16,
                  ml: 1,
                  color: 'text.secondary',
                  cursor: 'help',
                }}
              />
            </Tooltip>
          </Box>
          {/* Σ Gesamt Newsletter — sums every zone (full breakdown, ignores
              the zone filter so the user sees the unfiltered total here).
              ETKP denominator is `newsletter_recipients` from the API — the
              outer summary.total_emails_sent SUMs across all selected
              categories so it would understate this card's ETKP. Distinct
              visual treatment: deeper background + accent border. */}
          {(() => {
            const z = summary.newsletter_zones;
            if (!z) return null;
            const total = z.ki_ad + z.checkpoint + z.bottom + z.ps + z.html_box + z.unknown;
            const sent = Number(summary.newsletter_recipients ?? summary.total_emails_sent ?? 0);
            const totalEtkp = sent > 0 ? (total / sent) * 1000 : 0;
            return (
              <Card
                sx={{
                  backgroundColor: colors.primary[500],
                  border: `1px solid ${colors.greenAccent[400]}`,
                  mb: 2,
                }}
              >
                <CardContent
                  sx={{
                    p: 1.5,
                    '&:last-child': { pb: 1.5 },
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 3,
                  }}
                >
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ color: colors.greenAccent[400], fontWeight: 700 }}
                    >
                      Σ GESAMT NEWSLETTER
                    </Typography>
                    <Typography variant="h4" fontWeight="700" color={colors.grey[100]}>
                      €
                      {total.toLocaleString('de-DE', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: colors.greenAccent[300] }}>
                      Ø ETKP
                    </Typography>
                    <Typography variant="h5" fontWeight="600" color={colors.greenAccent[300]}>
                      €
                      {totalEtkp.toLocaleString('de-DE', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                      <Typography component="span" variant="caption" sx={{ ml: 0.5 }}>
                        / 1.000
                      </Typography>
                    </Typography>
                  </Box>
                  <Box sx={{ ml: 'auto' }}>
                    <Typography variant="caption" color="text.secondary">
                      Empfänger: {sent.toLocaleString('de-DE')}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            );
          })()}
          <Grid container spacing={2}>
            {NEWSLETTER_ZONES.map((zone) => {
              const value = summary.newsletter_zones?.[zone] ?? 0;
              // Use newsletter-only recipients so per-zone ETKP is correct
              // when other categories are also in the type filter.
              const sent = Number(summary.newsletter_recipients ?? summary.total_emails_sent ?? 0);
              const zoneEtkp = sent > 0 ? (value / sent) * 1000 : 0;
              const isFiltered = !selectedZones.includes(zone);
              const colorMap: Record<string, string> = {
                ki_ad: colors.blueAccent[400],
                checkpoint: colors.greenAccent[400],
                bottom: colors.greenAccent[300],
                ps: colors.redAccent[400],
                html_box: '#9c27b0',
                unknown: colors.grey[400],
              };
              return (
                <Grid item xs={6} sm={4} md={2} key={zone}>
                  <Card
                    sx={{
                      backgroundColor: colors.primary[400],
                      border: `1px solid ${colors.grey[700]}`,
                      opacity: isFiltered ? 0.4 : 1,
                    }}
                  >
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="caption" sx={{ color: colorMap[zone], fontWeight: 600 }}>
                        {ZONE_LABELS[zone]}
                      </Typography>
                      <Typography variant="h6" fontWeight="600" color={colors.grey[100]}>
                        €
                        {value.toLocaleString('de-DE', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </Typography>
                      <Typography variant="caption" sx={{ color: colors.greenAccent[400] }}>
                        ETKP €
                        {zoneEtkp.toLocaleString('de-DE', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </Typography>
                      {isFiltered ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block' }}
                        >
                          (gefiltert)
                        </Typography>
                      ) : null}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Paper>
      ) : null}

      {/* Newsletter ETKP-Entwicklung — stacked bar per period, zone-coloured.
          Renders only when newsletter is in type filter AND we have zone data
          on at least one period. Reuses the data array (already has
          newsletter_zones per period from /api/etkp-development). */}
      {summary?.newsletter_zones &&
      selectedMailingTypes.includes('newsletter') &&
      data.some((d) => d.newsletter_zones) ? (
        <Paper elevation={3} sx={{ p: 2, mb: 3, backgroundColor: colors.primary[400] }}>
          <Box display="flex" alignItems="center" mb={1.5}>
            <Typography variant="h5" color={colors.grey[100]}>
              Newsletter ETKP-Entwicklung (gestapelt je Zone)
            </Typography>
            <Tooltip title="ETKP je Zone über die Zeit — gestapelt summiert sich zur Gesamt-Newsletter-ETKP pro Periode. Y-Achse: € pro 1.000 Empfänger. Hover für Detailwerte.">
              <InfoOutlined
                sx={{
                  fontSize: 16,
                  ml: 1,
                  color: 'text.secondary',
                  cursor: 'help',
                }}
              />
            </Tooltip>
          </Box>
          <Box height="380px">
            <ResponsiveBar
              data={data.map((d) => {
                // Newsletter-only recipients per period — correct denominator
                // for zone-ETKP. d.emails_sent is the all-category SUM and
                // would understate ETKP when sam/heatup/etc. are also selected.
                const sent = Number(d.newsletter_recipients ?? d.emails_sent ?? 0);
                const zones = d.newsletter_zones || ({} as ZoneRevenue);
                const result: Record<string, string | number> = { period: d.period };
                for (const z of NEWSLETTER_ZONES) {
                  const r = zones[z] ?? 0;
                  result[z] = sent > 0 ? Number(((r / sent) * 1000).toFixed(2)) : 0;
                }
                return result;
              })}
              keys={[...NEWSLETTER_ZONES]}
              indexBy="period"
              margin={{ top: 16, right: 130, bottom: 60, left: 60 }}
              padding={0.25}
              groupMode="stacked"
              colors={({ id }: { id: string | number }) =>
                ({
                  ki_ad: colors.blueAccent[400],
                  checkpoint: colors.greenAccent[400],
                  bottom: colors.greenAccent[300],
                  ps: colors.redAccent[400],
                  html_box: '#9c27b0',
                  unknown: colors.grey[400],
                })[String(id)] || colors.grey[500]
              }
              theme={{
                axis: {
                  ticks: { text: { fill: colors.grey[100] } },
                  legend: { text: { fill: colors.grey[100] } },
                },
                legends: { text: { fill: colors.grey[100] } },
                tooltip: { container: { color: colors.primary[500] } },
              }}
              axisBottom={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: -30,
                format: (v: string | number) => getFormattedTick(aggregation, String(v)),
              }}
              axisLeft={{
                tickSize: 5,
                tickPadding: 5,
                legend: 'ETKP €/1000',
                legendPosition: 'middle',
                legendOffset: -50,
                format: (v: string | number) => `€${v}`,
              }}
              labelSkipHeight={14}
              tooltip={({
                id,
                value,
                indexValue,
              }: {
                id: string | number;
                value: number;
                indexValue: string | number;
              }) => (
                <Box sx={{ background: '#fff', p: 1, fontSize: 12 }}>
                  <strong>{getFormattedTick(aggregation, String(indexValue))}</strong>
                  <br />
                  {ZONE_LABELS[String(id)]}:{' '}
                  <strong>
                    €{Number(value).toLocaleString('de-DE', { minimumFractionDigits: 2 })}
                  </strong>
                </Box>
              )}
              legends={[
                {
                  dataFrom: 'keys',
                  anchor: 'bottom-right',
                  direction: 'column',
                  translateX: 120,
                  itemWidth: 100,
                  itemHeight: 18,
                  itemsSpacing: 2,
                  symbolSize: 14,
                  data: NEWSLETTER_ZONES.map((z, i) => ({
                    id: z,
                    label: ZONE_LABELS[z],
                    color:
                      (
                        {
                          ki_ad: colors.blueAccent[400],
                          checkpoint: colors.greenAccent[400],
                          bottom: colors.greenAccent[300],
                          ps: colors.redAccent[400],
                          html_box: '#9c27b0',
                          unknown: colors.grey[400],
                        } as Record<string, string>
                      )[z] || colors.grey[500],
                  })),
                },
              ]}
            />
          </Box>
        </Paper>
      ) : null}

      <EtkpDetailModal
        open={selectedPeriod !== null}
        onClose={() => setSelectedPeriod(null)}
        period={selectedPeriod}
        aggregation={aggregation}
        startDate={filterState.startDate}
        endDate={filterState.endDate}
        selectedCustomers={selectedCustomers}
        selectedMailingTypes={selectedMailingTypes}
        selectedZones={selectedZones}
        normalizeInactive={normalizeInactive}
      />
    </Box>
  );
};

export default EtkpDevelopment;
